<template>
    <form class="form" @submit.prevent="$emit('submit')" @reset="$emit('reset')">
        <!-- Moment box -->
        <div class="row">
            <div class="col-12 col-xl-9">
                <div class="block has-no-bottom-margin">
                    <!-- Input form -->
                    <div>
                        <div class="row">
                            <div class="col-12">
                                <h4 class="has-no-margin" v-html="title"></h4>
                                <span class="text-italic">{{$t('form.appointment.header_multiple_allowed')}}</span>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12 col-xxl-7">
                                <datepicker class="datepicker" v-model="editorMoment.date" :inline="true" :highlighted="highlightDates" :disabled-dates="{to: new Date()}"></datepicker>
                            </div>
                            <div class="col-12 col-xxl-5">
                                <p><strong>{{$t('form.appointment.header_slots')}}</strong></p>

                                <div class="checkbox" v-for="slot in slots">
                                    <input class="checkbox__cb" :id="slot" type="checkbox"
                                           v-model="editorMoment.slots[slot]"/>
                                    <label class="checkbox__label--emph" :for="slot">
                                        {{$t('form.appointment.label_' + slot)}}
                                        <span>{{$t('form.appointment.label_' + slot + '_time')}}</span>
                                    </label>
                                </div>

                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12">
                                <button type="button" class="button--cta" @click="addMoment" :disabled="!canAdd">
                                    {{$t('form.appointment.add_date')}}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <table class="table--list">
                    <tbody>
                        <tr v-for="moment in moments">
                            <td class="table__cell--date">
                                <p>{{$t('form.appointment.label_moment_date')}}</p>
                                <span>{{formatDate(moment)}}</span>
                                <div class="table__row visible-xs">
                                    <br/>
                                    <p>{{$t('form.appointment.label_moment_slots')}}</p>
                                    <span>{{formatSlots(moment)}}</span>
                                </div>
                            </td>
                            <td class="hidden-xs">
                                <p>{{$t('form.appointment.label_moment_slots')}}</p>
                                <span>{{formatSlots(moment)}}</span>
                            </td>
                            <td>
                                <a @click.stop="removeMoment(moment)"  v-tooltip="$t('common.button.remove')"
                                   class="button--icon">
                                    <i class="mdi mdi-trash-can"></i>
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="row">
            <!-- Address box -->
            <div class="col-12 col-xl-9 col-xxl-5 align-items-stretch flex">
                <div class="block has-no-top-margin">
                    <h4>{{$t('form.appointment.address')}}</h4>
                    <div v-for="(value, field) in address" class="form__group">
                        <i class="form__input__icon mdi mdi-account"></i>
                        <input v-gen.id="field" v-model="address[field]"
                               class="form__input--text underline has-icon"
                               :placeholder="$t('form.appointment.label_' + field)">
                    </div>
                </div>
            </div>
            <!-- Comment box -->
            <div class="col-12 col-xl-9 col-xxl-4 align-items-stretch flex">
                <div class="block has-no-top-margin">
                    <h4 v-gen:comment.for >{{$t('form.appointment.label_comment')}}</h4>
                    <textarea v-gen:comment.id class="block__textarea form__input--textarea" v-model="comment"
                    :placeholder="$t('form.appointment.placeholder_comment')"></textarea>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div class="form__buttons">
                    <button type="submit" class="button--cta" :disabled="!canSubmit" :class="{'is-loading': busy}">
                        {{$t('form.appointment.button_submit')}}
                    </button>
                    <button type="reset" class="button" :disabled="busy">
                        {{$t('common.button.go_back')}}
                    </button>
                </div>
            </div>
        </div>
    </form>
</template>
<script>
    import Vue from 'vue';
    import {Component, Prop} from 'vue-property-decorator';
    import moment from 'moment';
    import _ from 'lodash';
    import Datepicker from '../general/Datepicker';

    export const SLOT_MORNING = 'morning';
    export const SLOT_AFTERNOON = 'afternoon';
    export const SLOT_EVENING = 'evening';

    @Component({
        components: {Datepicker}
    })
    export default class AppointmentForm extends Vue {
        @Prop({type: String})
        title;

        @Prop({type: Boolean, default: false})
        busy;

        @Prop({type: Object, default: () => ({})})
        appointment;

        get moments() {
            if (!this.appointment) return [];
            if (!this.appointment.moments) {
                this.appointment.moments = {};
            }
            return _.map(this.appointment.moments, (value, key) => ({date: new Date(key), slots: value}));
        }

        get address() {
            if (!this.appointment) return {};
            if (!this.appointment.address) {
                this.appointment.address = {
                    street: '',
                    number: '',
                    postalCode: '',
                    city: '',
                    country: '',
                };
            }
            return this.appointment.address;
        }

        get comment() {
            if (!this.appointment) return '';
            return this.appointment.comment;
        }

        set comment(value) {
            this.appointment.comment = value;
        }

        get slots() {
            return [
                SLOT_MORNING,
                SLOT_AFTERNOON,
                SLOT_EVENING,
            ];
        }

        get highlightDates() {
            return {
                dates: this.moments.map(moment => moment.date),
            };
        }

        editorMoment = {
            date: moment().add(1, 'days').toDate(),
            slots: {
                [SLOT_MORNING]: true,
                [SLOT_AFTERNOON]: true,
                [SLOT_EVENING]: false,
            },
        };

        addMoment() {
            if (!this.canAdd) return;
            const date = this.editorMoment.date;
            const slots = _.map(this.editorMoment.slots, (value, key) => value ? key : false).filter(item => item);
            const key = moment(date).format('YYYY-MM-DD');
            if (this.appointment.moments.hasOwnProperty(key)) {
                this.appointment.moments[key] = slots;
            } else {
                Vue.set(this.appointment.moments, key, slots);
            }
        }

        removeMoment({date}) {
            // Passed through object because then objects from GET_MOMENTS can be passed directly
            const key = moment(date).format('YYYY-MM-DD');
            Vue.delete(this.appointment.moments, key);
        }

        formatDate(item) {
            if (!item || !item.date) return undefined;
            return moment(item.date).format('dddd LL');
        }

        formatSlots(item) {
            if (!item || !item.slots) return undefined;
            return item.slots
                .map(slot => this.$t('form.appointment.label_' + slot))
                .join(', ');
        }

        get canAdd() {
            const date = moment(this.editorMoment.date);
            const slots = _.map(this.editorMoment.slots, (value, key) => value ? key : false).filter(item => item);
            return date.isAfter(moment()) && slots.length;
        }

        get canSubmit() {
            return !this.busy && this.moments.length;
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/forms/AppointmentForm.vue